/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Typography,
    Container,
    Alert,
    Box,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import axiosInstance from '../../helper/AxiosInstance';
import jsPDF from "jspdf";

const CertificateGenerator = () => {
    const [userList, setUserList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [selectedTrack, setSelectedTrack] = useState(1);
    const [generating, setGenerating] = useState(false);

    const userData = JSON.parse(localStorage.getItem('userData'));

    // Reusable function to fetch participants by track
    const fetchParticipants = async (track) => {
        try {
            const res = await axiosInstance.get(`/scoring/fetchprasentationbytrack?track=${track}`);
            if (res.data && res.data.userList) {
                setUserList(res.data.userList);
                setError(null);
            } else {
                setUserList([]);
                setError("No user data found");
            }
        } catch (err) {
            console.error("Error fetching participants:", err);
            setError("Failed to load participants");
        }
    };

    // Initial fetch when component mounts
    useEffect(() => {
        setLoading(true);
        fetchParticipants(userData.userTrack).finally(() => setLoading(false));
    }, [userData.userTrack]);

    // Positioning constants (adjust these values as needed)
    const NAME_FONT_SIZE = 60;   // px
    const TITLE_FONT_SIZE = 50;  // px
    const NAME_Y = 510;          // px from top
    const TITLE_Y = 635;         // px from top
    const TITLE_MAX_WIDTH = 2000; // wrap width in px
    const TITLE_LINE_HEIGHT = 70; // px

    const handleDownload = async (user) => {
        setGenerating(true);

        try {
            // Load the certificate background image
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = "/certificate-01.jpg";

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                setTimeout(() => {
                    if (!img.complete) reject(new Error("Image loading timed out"));
                }, 5000);
            });

            // Create PDF document
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [img.width, img.height]
            });

            // Add background image
            pdf.addImage(img, 'JPEG', 0, 0, img.width, img.height);

            // Set font styles for name and organization
            pdf.setFontSize(NAME_FONT_SIZE);
            pdf.setFont("Arial", "bold");
            pdf.setTextColor(223, 250, 60); // #dffa3c

            // Format: "Name, Organization Name"
            const nameWithOrg = `${user.authorName}, ${user.organization || "Organization Name"}`;

            // Center text horizontally
            const nameWidth = pdf.getTextWidth(nameWithOrg);
            const nameX = (img.width - nameWidth) / 2;

            // Add name and organization to PDF
            pdf.text(nameWithOrg, nameX, NAME_Y);

            // Set font styles for title
            pdf.setFontSize(TITLE_FONT_SIZE);
            pdf.setFont("Arial", "bold");
            pdf.setTextColor(136, 194, 106); // #88c26a

            const title = user.title;

            // Split text into lines
            const lines = pdf.splitTextToSize(title, TITLE_MAX_WIDTH);

            // Calculate starting Y position for title
            const titleHeight = lines.length * TITLE_LINE_HEIGHT;
            const titleStartY = TITLE_Y - (titleHeight / 2) + (TITLE_LINE_HEIGHT / 2);

            // Add each line of text
            lines.forEach((line, index) => {
                const lineWidth = pdf.getTextWidth(line);
                const lineX = (img.width - lineWidth) / 2;
                pdf.text(line, lineX, titleStartY + (index * TITLE_LINE_HEIGHT));
            });

            // Save PDF
            pdf.save(`certificate-${user.authorName}.pdf`);

        } catch (error) {
            console.error("Error generating certificate:", error);
        } finally {
            setGenerating(false);
        }
    };

    const getTrackName = (trackNumber) => {
        const trackNames = {
            1: "Structures & Materials 1",
            2: "Structures & Materials 2",
            3: "Vehicle Dynamics",
            4: "System Dynamics & Machinery",
            5: "Fluids & Co-Simulation",
            6: "Virtual Manufacturing"
        };
        return trackNames[trackNumber] || `Track ${trackNumber}`;
    };

    // On track change
    const handleTrackChange = async (e) => {
        const track = e.target.value;
        setSelectedTrack(track);
        setLoadingParticipants(true);

        try {
            await fetchParticipants(track);
        } finally {
            setLoadingParticipants(false);
        }
    };

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h5" sx={{ fontSize: 20, fontWeight: 600 }}>
                    Participant Certificates
                </Typography>

                {/* Tracks dropdown */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                        value={selectedTrack}
                        onChange={handleTrackChange}
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            minWidth: 200,
                            fontWeight: '600',
                            fontSize: '0.95rem',
                        }}
                    >
                        {[1, 2, 3, 4, 5, 6].map((track) => (
                            <MenuItem
                                key={track}
                                value={track}
                                sx={{ fontSize: '0.95rem', fontWeight: 600 }}
                            >
                                Track - {getTrackName(track)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Table Section */}
            {loadingParticipants ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ background: "linear-gradient(45deg, #a8edea, #fed6e3)" }}>
                                <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>Author Name</TableCell>
                                <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>Paper Title</TableCell>
                                <TableCell sx={{ fontSize: 16, fontWeight: 600 }} align="center">Day</TableCell>
                                <TableCell sx={{ fontSize: 16, fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {userList.length > 0 ? (
                                userList.map((user, index) => (
                                    <TableRow key={user.id || index}>
                                        <TableCell sx={{ fontSize: 18, fontWeight: 500 }}>{user.authorName}</TableCell>
                                        <TableCell sx={{ fontSize: 18, fontWeight: 500 }}>{user.title}</TableCell>
                                        <TableCell sx={{ fontSize: 18, fontWeight: 500 }} align="center">
                                            {user.day || "Day 1"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="contained"
                                                startIcon={<Download />}
                                                onClick={() => handleDownload(user)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#4caf50',
                                                    '&:hover': { backgroundColor: '#388e3c' },
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Download
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ fontSize: 15, fontWeight: 600, color: 'gray' }}>
                                        No participants found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
};

export default CertificateGenerator;